import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Film, Tv, Star, Clock, Tag as TagIcon, ArrowLeft } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { SEOHead } from '../components/seo/SEOHead';
import { buildSEOTitle } from '../utils/seo';
import { BlogPost } from '../types/blog';
import { Button } from '../components/common/Button';
import { Language } from '../types/language';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { useTranslation } from 'react-i18next';
import { AdBlock } from '../components/ads/AdBlock';
import blogService from "../services/blogService";
import { LoadingSpinner } from "../components/common/LoadingSpinner";
import { useScrollToTopOnMount } from "../hooks/useScrollToTop";

const Container = styled.div`
  margin: 0 auto;
  position: relative;
`;

const BackButton = styled(Link)`
  position: absolute;
  top: 10px;
  left: 10px;
  width: 40px;
  height: 40px;
  background: var(--color-primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  text-decoration: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  z-index: 10;
  
  &:hover {
    background: var(--color-primary-dark);
    transform: scale(1.1);
  }
  
  @media (max-width: 768px) {
    top: 8px;
    left: 8px;
    width: 36px;
    height: 36px;
  }
`;

const ImageSection = styled.div`
  background-color: #000;
  padding: 40px 20px;
  margin-bottom: 0;
  
  @media (max-width: 768px) {
    padding: 20px 10px;
  }
`;

const ContentSection = styled.div`
  background: #fff;
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
  border-radius: 8px 8px 0 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-top: -20px;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 15px;
    margin-top: -15px;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin: 20px 0;
  color: #1a1a1a;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin: 15px 0;
  }
`;

const MetaContainer = styled.div`
  display: flex;
  gap: 20px;
  margin: 20px 0;
  align-items: center;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 20px 0;
`;

const Tag = styled.span`
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  padding: 5px 10px;
  border-radius: 20px;
  font-size: 0.9rem;
`;

const Rating = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  color: #e2b616;
  font-weight: bold;
  font-size: 1.1rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const Content = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #333;
  margin-top: 30px;
  margin-bottom: var(--spacing-4);
  
  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.6;
    margin-top: 20px;
  }
  
  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 20px 0;
  }
`;

const BackLink = styled(Link)`
  display: inline-block;
  margin-top: 30px;
  color: #e2b616;
  text-decoration: none;
  font-weight: 500;
  
  @media (max-width: 768px) {
    margin-top: 20px;
  }
  
  &:hover {
    text-decoration: underline;
  }
`;

const AdminActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-2);
  margin-bottom: var(--spacing-4);
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: var(--spacing-1);
  }
`;

const ShareSection = styled.div`
  display: flex;
  justify-content: center;
  margin: var(--spacing-4) 0;
  padding: var(--spacing-4) 0;
  border-top: 1px solid var(--color-border);
  width: 100%;
  
  @media (max-width: 768px) {
    margin: var(--spacing-3) 0;
    padding: var(--spacing-3) 0;
  }
`;

const HeaderContainer = styled.div`
  position: relative;
  margin-bottom: var(--spacing-4);
  
  @media (max-width: 768px) {
    margin-bottom: var(--spacing-3);
  }
`;

const LanguageButtons = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    position: static;
    justify-content: center;
    margin-bottom: var(--spacing-2);
  }
`;

const LanguageButton = styled.button<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background: rgba(255, 255, 255, 0.05);
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.9rem;
  border: 1px solid ${props => props.$isActive ? 'var(--color-primary)' : 'transparent'};
  cursor: pointer;
  color: ${props => props.$isActive ? 'var(--color-primary)' : 'var(--color-text-secondary)'};
  transition: all 0.2s ease;
  
  @media (max-width: 768px) {
    padding: 0.35rem 0.75rem;
    font-size: 0.8rem;
  }

  &:hover {
    color: var(--color-primary);
    border-color: var(--color-primary);
  }
`;

const Overview = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #333;
  margin-top: 30px;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.6;
    margin-top: 20px;
  }
`;

const BlogPostContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const AdContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
  margin: var(--spacing-4) 0;
`;

const BlogPostDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [currentLang, setCurrentLang] = useState<Language>(Language.FR);
  
  // Scroll automatique vers le haut au chargement
  useScrollToTopOnMount();
  const { user } = useSelector((state: RootState) => state.auth);
  const { t } = useTranslation('blog');
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    const fetchPost = async () => {
      if (id) {
        try {
          const data = await blogService.getPostById(id);
          setPost(data);
        } catch (error) {
          console.error('Error fetching post:', error);
        }
      }
    };

    fetchPost();
  }, [id]);

  if (!post) {
    return (
      <Layout>
        <SEOHead
          title={t('post.loading')}
          description={t('post.description')}
          type="website"
        />
        <BlogPostContainer>
          <AdContainer>
            <AdBlock format="banner" />
          </AdContainer>
          <LoadingSpinner />
          <AdContainer>
            <AdBlock format="banner" />
          </AdContainer>
        </BlogPostContainer>
      </Layout>
    );
  }

  const displayContent = currentLang === Language.FR ? {
    title: post.title,
    content: post.content,
    overview: post.overview
  } : {
    title: post.titleEn || post.title,
    content: post.contentEn || post.content,
    overview: post.overviewEn || post.overview
  };

  const shortHook = (displayContent.overview || '')
    .split(/[.!?]/)[0]
    .trim();
  const tagsLc = (post.tags || []).map(t => t.toLowerCase());
  const contentPrefix = tagsLc.includes('explication fin') || tagsLc.includes("explication de fin")
    ? 'Explication de fin de'
    : tagsLc.includes('ordre chronologique')
      ? 'Ordre chronologique de'
      : (post.rating ? 'Critique de' : 'Avis sur');
  const seoTitle = buildSEOTitle({
    baseTitle: displayContent.title,
    year: post.year,
    prefix: contentPrefix,
    hook: shortHook || undefined,
  });

  const handleShare = async () => {
    if (!navigator.share) {
      // Fallback pour les navigateurs qui ne supportent pas l'API Web Share
      const tempInput = document.createElement('input');
      tempInput.value = window.location.href;
      document.body.appendChild(tempInput);
      tempInput.select();
      document.execCommand('copy');
      document.body.removeChild(tempInput);
      alert(t('post.copied'));
      return;
    }

    try {
      await navigator.share({
        title: displayContent.title,
        text: displayContent.overview,
        url: window.location.href
      });
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  return (
    <Layout>
      <SEOHead
        title={seoTitle}
        description={displayContent.overview || displayContent.content.substring(0, 160)}
        type="website"
      />
      <BlogPostContainer>
        <AdContainer>
          <AdBlock format="banner" />
        </AdContainer>

        <Container>
          <ImageSection>
            {post.image && (
              <img 
                src={post.image}
                alt={displayContent.title}
                style={{ width: '100%', maxWidth: '1200px', margin: '0 auto', display: 'block' }}
              />
            )}
          </ImageSection>

          <ContentSection>
            <BackButton to="/blog">
              <ArrowLeft size={20} />
            </BackButton>
            
            <LanguageButtons>
              <LanguageButton 
                $isActive={currentLang === Language.FR} 
                onClick={() => setCurrentLang(Language.FR)}
              >
                FR
              </LanguageButton>
              <LanguageButton 
                $isActive={currentLang === Language.EN} 
                onClick={() => setCurrentLang(Language.EN)}
              >
                EN
              </LanguageButton>
            </LanguageButtons>

            <Title>{displayContent.title}</Title>

            <MetaContainer>
              {post.type === "film" ? (
                <MetaItem><Film size={20} /> Film</MetaItem>
              ) : (
                <MetaItem><Tv size={20} /> Série</MetaItem>
              )}
              {post.rating && (
                <MetaItem>
                  <Star size={20} />
                  <span>{post.rating}/10</span>
                </MetaItem>
              )}
              {post.year && (
                <MetaItem>
                  <Clock size={20} />
                  <span>{post.year}</span>
                </MetaItem>
              )}
            </MetaContainer>

            {post.tags && post.tags.length > 0 && (
              <TagsContainer>
                {post.tags.map((tag) => (
                  <Tag key={tag}>
                    <TagIcon size={14} /> {tag}
                  </Tag>
                ))}
              </TagsContainer>
            )}

            <Overview>{displayContent.overview}</Overview>

            <Content dangerouslySetInnerHTML={{ __html: displayContent.content }} />

            <ShareSection>
              <Button onClick={handleShare}>
                {t('post.share')}
              </Button>
            </ShareSection>

            {isAdmin && (
              <AdminActions>
                <Link to={`/blog/edit/${post.id}`}>
                  <Button>
                    {t('post.edit')}
                  </Button>
                </Link>
              </AdminActions>
            )}
          </ContentSection>
        </Container>

        <AdContainer>
          <AdBlock format="banner" />
        </AdContainer>
      </BlogPostContainer>
    </Layout>
  );
};

export default BlogPostDetail;
